/**
 * setting-dialog.tsx — Hộp thoại chi tiết cho một dòng cài đặt (settings-row.tsx):
 * tiêu đề = tên cài đặt, mô tả 1 dòng, công tắc bật (nếu có) ở đầu, các trường, nút Huỷ / Lưu.
 *
 * Giá trị được sửa trên BẢN SAO (`draft`) — bấm Lưu mới ghi vào form nháp (rồi tự lưu); Huỷ bỏ thay đổi.
 * `validate` trả thông báo lỗi (tiếng Việt) để chặn Lưu khi dữ liệu chưa hợp lệ.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

export function SettingDialog<T>({ open, onOpenChange, title, description, icon, value, onSave, enabledKey, enabledLabel, validate, children, canEdit = true, size = 'md', footerNote }: {
  open: boolean
  onOpenChange: (o: boolean) => void
  title: ReactNode
  description?: ReactNode
  icon?: ReactNode
  value: T
  onSave: (v: T) => void
  /** Có công tắc bật/tắt ở đầu hộp: hàm đọc / ghi cờ bật trên draft. */
  enabledKey?: { get: (v: T) => boolean; set: (v: T, on: boolean) => T }
  enabledLabel?: string
  validate?: (v: T) => string | null
  /** Nội dung các trường — nhận draft + setter. */
  children: (draft: T, set: (fn: (d: T) => T) => void, enabled: boolean) => ReactNode
  canEdit?: boolean
  size?: 'md' | 'lg' | 'xl'
  footerNote?: ReactNode
}) {
  const { t } = useTranslation('aiTrainUi')
  const [draft, setDraft] = useState<T>(value)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => { if (open) { setDraft(value); setError(null) } }, [open, value])
  const enabled = enabledKey ? enabledKey.get(draft) : true
  const set = (fn: (d: T) => T) => { setDraft((d) => fn(d)); setError(null) }
  const save = () => {
    const problem = validate?.(draft) ?? null
    if (problem) { setError(problem); return }
    onSave(draft)
    onOpenChange(false)
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(size === 'xl' ? 'sm:max-w-3xl' : size === 'lg' ? 'sm:max-w-2xl' : 'sm:max-w-lg', 'max-h-[92vh] overflow-y-auto')}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{icon && <span className="text-primary">{icon}</span>}{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {enabledKey && (
          <label className="flex items-center justify-between rounded-lg border bg-muted/30 px-3 py-2.5 text-sm">
            <span className="font-medium">{enabledLabel ?? t('settingDialog.enable')}</span>
            <Switch checked={enabled} onCheckedChange={(on) => set((d) => enabledKey.set(d, on))} disabled={!canEdit} />
          </label>
        )}
        <div className={cn('space-y-3', enabledKey && !enabled && 'pointer-events-none opacity-50')}>
          {children(draft, set, enabled)}
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        {footerNote && <p className="text-[11px] text-muted-foreground">{footerNote}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{canEdit ? t('common:actions.cancel') : t('common:actions.close')}</Button>
          {canEdit && <Button onClick={save}>{t('common:actions.save')}</Button>}
        </div>
      </DialogContent>
    </Dialog>
  )
}
